/**
 * UploadCSVModal.tsx
 * 
 * Modal completo para importar contactos desde CSV.
 * - Drag & Drop
 * - Preview de datos
 * - Mapeo de columnas
 * - Validación
 * - Detección de duplicados
 * - Progress bar
 * 
 * @version 1.0.0
 * @date 2024-11-06
 */

import React, { useState, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { Modal, Alert, ProgressBar, Form } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFileUpload,
  faCheckCircle,
  faExclamationTriangle,
  faTimesCircle,
  faDownload,
  faArrowRight,
  faArrowLeft,
  faSpinner
} from '@fortawesome/free-solid-svg-icons';

import { useCSVImport, ColumnMapping } from '../../../hooks/useCSVImport';
import { useContactsDatabase } from '../../../hooks/useContactsDatabase';
import styles from '../../../types/ContactsHubSpot.module.css';

// ==================== 📋 INTERFACES ====================

interface UploadCSVModalProps {
  show: boolean;
  onHide: () => void;
  onSuccess: (count: number) => void;
}

type ModalStep = 'upload' | 'mapping' | 'preview' | 'importing' | 'complete';

// ==================== 🎨 COMPONENT ====================

const UploadCSVModal: React.FC<UploadCSVModalProps> = ({ show, onHide, onSuccess }) => {
  
  // Hooks
  const {
    isProcessing,
    progress,
    currentStep: importStep,
    rawData,
    parsedContacts,
    columnMapping,
    detectedColumns,
    importResult,
    parseCSV,
    setColumnMapping,
    validateContacts,
    importContacts,
    reset
  } = useCSVImport();

  const { contacts, addContact } = useContactsDatabase();

  // Estados
  const [modalStep, setModalStep] = useState<ModalStep>('upload');
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // ==================== 📤 HANDLERS ====================

  const handleFileSelect = useCallback(async (file: File) => {
    setError(null);
    
    // Validar tipo
    if (!file.name.endsWith('.csv')) {
      setError('Por favor selecciona un archivo CSV');
      return;
    }
    
    // Validar tamaño (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      setError('El archivo es demasiado grande (máximo 10MB)');
      return;
    }
    
    try {
      await parseCSV(file);
      setModalStep('mapping');
    } catch (err: any) {
      setError(err.message || 'Error al leer el archivo');
    }
  }, [parseCSV]);

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    const file = e.dataTransfer.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
  }, [handleFileSelect]);

  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    e.stopPropagation();
    
    const file = e.target.files?.[0];
    if (file) {
      handleFileSelect(file);
    }
    
    // Reset input para permitir re-selección del mismo archivo
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [handleFileSelect]);

  const handleColumnMappingChange = useCallback((field: keyof ColumnMapping, value: string) => {
    setColumnMapping({
      ...columnMapping,
      [field]: value || undefined
    });
  }, [columnMapping, setColumnMapping]);

  const handleNextFromMapping = useCallback(() => {
    // Validar que al menos nombre y teléfono estén mapeados
    if (!columnMapping.name || !columnMapping.phone) {
      setError('Debes mapear al menos Nombre y Teléfono');
      return;
    }
    
    // Validar contactos
    validateContacts(contacts);
    setModalStep('preview');
  }, [columnMapping, validateContacts, contacts]);

  const handleImport = useCallback(async () => {
    setModalStep('importing');
    
    try {
      const result = await importContacts(addContact);
      setModalStep('complete');
      
      if (result.success > 0) {
        onSuccess(result.success);
      }
    } catch (err: any) {
      setError(err.message || 'Error al importar contactos');
      setModalStep('preview');
    }
  }, [importContacts, addContact, onSuccess]);

  const handleClose = useCallback(() => {
    reset();
    setModalStep('upload');
    setError(null);
    setDragActive(false);
    onHide();
  }, [reset, onHide]);

  const handleDownloadTemplate = useCallback(() => {
    const csv = 'nombre,telefono,email,empresa,tags,grupos\n' +
                'Juan Pérez,+56912345678,juan@example.com,Tech Corp,vip;cliente,grupo1\n' +
                'María González,+56987654321,maria@example.com,StartUp,,grupo2';
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'plantilla_contactos.csv';
    a.click();
    URL.revokeObjectURL(url);
  }, []);

  // ==================== 🎨 RENDER: UPLOAD STEP ====================

  const renderUploadStep = () => (
    <>
      <Modal.Header closeButton style={{ 
        backgroundColor: 'var(--bs-body-bg)',
        borderBottom: '1px solid var(--bs-border-color)'
      }}>
        <Modal.Title style={{ color: 'var(--bs-body-color)' }}>
          <FontAwesomeIcon icon={faFileUpload} className="me-2" />
          Importar Contactos
        </Modal.Title>
      </Modal.Header>
      
      <Modal.Body style={{ 
        backgroundColor: 'var(--bs-body-bg)',
        color: 'var(--bs-body-color)'
      }}>
        {error && (
          <Alert variant="danger" dismissible onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        
        <div
          className={styles.scrollable}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          style={{
            padding: '60px 20px',
            textAlign: 'center',
            border: dragActive ? '2px dashed var(--hs-orange)' : '2px dashed var(--bs-border-color)',
            borderRadius: '8px',
            backgroundColor: dragActive ? 'var(--hs-orange-light)' : 'var(--bs-secondary-bg)',
            cursor: 'pointer',
            transition: 'all 0.2s ease'
          }}
          onClick={() => fileInputRef.current?.click()}
        >
          <FontAwesomeIcon 
            icon={faFileUpload}
            style={{ 
              fontSize: '3rem', 
              color: dragActive ? 'var(--hs-orange)' : 'var(--bs-secondary-color)',
              marginBottom: '16px'
            }}
          />
          <h5 style={{ fontWeight: 600, marginBottom: '8px', color: 'var(--bs-body-color)' }}>
            {dragActive ? '¡Suelta el archivo aquí!' : 'Arrastra tu archivo CSV aquí'}
          </h5>
          <p style={{ color: 'var(--bs-secondary-color)', fontSize: '0.875rem', marginBottom: '16px' }}>
            o haz clic para seleccionar
          </p>
          <button 
            className={`${styles.btn} ${styles.btnPrimary}`}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              // Forzar click después de pequeño delay para asegurar que funcione
              setTimeout(() => {
                fileInputRef.current?.click();
              }, 10);
            }}
            type="button"
          >
            Seleccionar Archivo
          </button>
          
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            style={{ display: 'none', position: 'absolute', left: '-9999px' }}
            onChange={handleInputChange}
            onClick={(e) => e.stopPropagation()}
          />
        </div>
        
        <div style={{ 
          marginTop: '24px',
          padding: '20px',
          backgroundColor: 'var(--bs-secondary-bg)',
          borderRadius: '6px',
          border: '1px solid var(--bs-border-color)'
        }}>
          <h6 style={{ fontWeight: 600, marginBottom: '12px', fontSize: '0.813rem', color: 'var(--bs-body-color)' }}>
            📋 Formato requerido:
          </h6>
          <ul style={{ 
            fontSize: '0.813rem', 
            color: 'var(--bs-secondary-color)',
            paddingLeft: '20px',
            margin: 0
          }}>
            <li>Primera fila con encabezados (nombre, teléfono, email)</li>
            <li>Teléfonos con formato +56912345678</li>
            <li>Tags separados por punto y coma: tag1;tag2</li>
            <li>Máximo 10,000 contactos por archivo</li>
            <li>Formato: CSV (UTF-8)</li>
          </ul>
        </div>

        <div style={{ marginTop: '24px', textAlign: 'center' }}>
          <button 
            className={`${styles.btn} ${styles.btnSecondary} ${styles.btnSmall}`}
            onClick={handleDownloadTemplate}
            type="button"
          >
            <FontAwesomeIcon icon={faDownload} className="me-2" />
            Descargar Plantilla
          </button>
        </div>
      </Modal.Body>
    </>
  );

  // ==================== 🎨 RENDER: MAPPING STEP ====================

  const renderMappingStep = () => (
    <>
      <Modal.Header closeButton style={{ 
        backgroundColor: 'var(--bs-body-bg)',
        borderBottom: '1px solid var(--bs-border-color)'
      }}>
        <Modal.Title style={{ color: 'var(--bs-body-color)' }}>
          Mapeo de Columnas
        </Modal.Title>
      </Modal.Header>
      
      <Modal.Body className={styles.scrollable} style={{ 
        backgroundColor: 'var(--bs-body-bg)',
        color: 'var(--bs-body-color)'
      }}>
        {error && (
          <Alert variant="danger" dismissible onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        
        <Alert variant="info" style={{ fontSize: '0.875rem' }}>
          <strong>Paso 2 de 4:</strong> Mapea las columnas de tu CSV a los campos de contacto.
          Los campos marcados con <span style={{ color: 'red' }}>*</span> son obligatorios.
        </Alert>
        
        <p style={{ fontSize: '0.875rem', color: 'var(--hs-gray-600)', marginBottom: '24px' }}>
          Detectamos <strong>{rawData.length}</strong> filas en tu archivo.
        </p>
        
        <Form>
          <Form.Group className="mb-3">
            <Form.Label style={{ fontWeight: 600, fontSize: '0.875rem' }}>
              Nombre <span style={{ color: 'red' }}>*</span>
            </Form.Label>
            <Form.Select 
              value={columnMapping.name || ''}
              onChange={(e) => handleColumnMappingChange('name', e.target.value)}
              style={{ fontSize: '0.875rem' }}
            >
              <option value="">-- Seleccionar columna --</option>
              {detectedColumns.map(col => (
                <option key={col} value={col}>{col}</option>
              ))}
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label style={{ fontWeight: 600, fontSize: '0.875rem' }}>
              Teléfono <span style={{ color: 'red' }}>*</span>
            </Form.Label>
            <Form.Select 
              value={columnMapping.phone || ''}
              onChange={(e) => handleColumnMappingChange('phone', e.target.value)}
              style={{ fontSize: '0.875rem' }}
            >
              <option value="">-- Seleccionar columna --</option>
              {detectedColumns.map(col => (
                <option key={col} value={col}>{col}</option>
              ))}
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label style={{ fontWeight: 600, fontSize: '0.875rem' }}>
              Email
            </Form.Label>
            <Form.Select 
              value={columnMapping.email || ''}
              onChange={(e) => handleColumnMappingChange('email', e.target.value)}
              style={{ fontSize: '0.875rem' }}
            >
              <option value="">-- Seleccionar columna --</option>
              {detectedColumns.map(col => (
                <option key={col} value={col}>{col}</option>
              ))}
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label style={{ fontWeight: 600, fontSize: '0.875rem' }}>
              Empresa
            </Form.Label>
            <Form.Select 
              value={columnMapping.company || ''}
              onChange={(e) => handleColumnMappingChange('company', e.target.value)}
              style={{ fontSize: '0.875rem' }}
            >
              <option value="">-- Seleccionar columna --</option>
              {detectedColumns.map(col => (
                <option key={col} value={col}>{col}</option>
              ))}
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label style={{ fontWeight: 600, fontSize: '0.875rem' }}>
              Tags (separados por ; o ,)
            </Form.Label>
            <Form.Select 
              value={columnMapping.tags || ''}
              onChange={(e) => handleColumnMappingChange('tags', e.target.value)}
              style={{ fontSize: '0.875rem' }}
            >
              <option value="">-- Seleccionar columna --</option>
              {detectedColumns.map(col => (
                <option key={col} value={col}>{col}</option>
              ))}
            </Form.Select>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label style={{ fontWeight: 600, fontSize: '0.875rem' }}>
              Grupos (separados por ; o ,)
            </Form.Label>
            <Form.Select 
              value={columnMapping.groups || ''}
              onChange={(e) => handleColumnMappingChange('groups', e.target.value)}
              style={{ fontSize: '0.875rem' }}
            >
              <option value="">-- Seleccionar columna --</option>
              {detectedColumns.map(col => (
                <option key={col} value={col}>{col}</option>
              ))}
            </Form.Select>
          </Form.Group>
        </Form>
      </Modal.Body>
      
      <Modal.Footer>
        <button 
          className={`${styles.btn} ${styles.btnSecondary}`}
          onClick={() => setModalStep('upload')}
        >
          <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
          Atrás
        </button>
        <button 
          className={`${styles.btn} ${styles.btnPrimary}`}
          onClick={handleNextFromMapping}
          disabled={!columnMapping.name || !columnMapping.phone}
        >
          Siguiente
          <FontAwesomeIcon icon={faArrowRight} className="ms-2" />
        </button>
      </Modal.Footer>
    </>
  );

  // ==================== 🎨 RENDER: PREVIEW STEP ====================

  const renderPreviewStep = () => {
    const validCount = parsedContacts.filter(c => c.isValid).length;
    const duplicateCount = parsedContacts.filter(c => c.isDuplicate).length;
    const errorCount = parsedContacts.filter(c => c.errors.length > 0).length;
    
    return (
      <>
        <Modal.Header closeButton style={{ 
          backgroundColor: 'var(--bs-body-bg)',
          borderBottom: '1px solid var(--bs-border-color)'
        }}>
          <Modal.Title style={{ color: 'var(--bs-body-color)' }}>
            Vista Previa de Importación
          </Modal.Title>
        </Modal.Header>
        
        <Modal.Body className={styles.scrollable} style={{ 
          backgroundColor: 'var(--bs-body-bg)',
          color: 'var(--bs-body-color)'
        }}>
          <Alert variant="info" style={{ fontSize: '0.875rem' }}>
            <strong>Paso 3 de 4:</strong> Revisa los contactos antes de importar.
          </Alert>
          
          {/* Estadísticas */}
          <div style={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(3, 1fr)', 
            gap: '12px',
            marginBottom: '24px'
          }}>
            <div style={{ 
              padding: '16px', 
              backgroundColor: 'var(--hs-success)', 
              color: 'white',
              borderRadius: '8px',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '2rem', fontWeight: 'bold' }}>{validCount}</div>
              <div style={{ fontSize: '0.813rem' }}>Válidos</div>
            </div>
            
            <div style={{ 
              padding: '16px', 
              backgroundColor: 'var(--hs-warning)', 
              color: 'white',
              borderRadius: '8px',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '2rem', fontWeight: 'bold' }}>{duplicateCount}</div>
              <div style={{ fontSize: '0.813rem' }}>Duplicados</div>
            </div>
            
            <div style={{ 
              padding: '16px', 
              backgroundColor: 'var(--hs-danger)', 
              color: 'white',
              borderRadius: '8px',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '2rem', fontWeight: 'bold' }}>{errorCount}</div>
              <div style={{ fontSize: '0.813rem' }}>Con Errores</div>
            </div>
          </div>
          
          {/* Lista de contactos */}
          <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
            {parsedContacts.slice(0, 50).map((contact, index) => (
              <div 
                key={index}
                style={{
                  padding: '12px',
                  marginBottom: '8px',
                  border: '1px solid var(--hs-gray-200)',
                  borderRadius: '6px',
                  backgroundColor: contact.isValid ? 'white' : 'var(--hs-gray-50)'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <FontAwesomeIcon 
                    icon={
                      contact.isValid ? faCheckCircle :
                      contact.isDuplicate ? faExclamationTriangle :
                      faTimesCircle
                    }
                    style={{ 
                      color: 
                        contact.isValid ? 'var(--hs-success)' :
                        contact.isDuplicate ? 'var(--hs-warning)' :
                        'var(--hs-danger)'
                    }}
                  />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, fontSize: '0.875rem' }}>
                      {contact.name}
                    </div>
                    <div style={{ fontSize: '0.75rem', color: 'var(--hs-gray-600)' }}>
                      {contact.phone}
                      {contact.email && ` • ${contact.email}`}
                    </div>
                    {contact.errors.length > 0 && (
                      <div style={{ fontSize: '0.75rem', color: 'var(--hs-danger)', marginTop: '4px' }}>
                        {contact.errors.join(', ')}
                      </div>
                    )}
                    {contact.isDuplicate && (
                      <div style={{ fontSize: '0.75rem', color: 'var(--hs-warning)', marginTop: '4px' }}>
                        Duplicado - ya existe en la base de datos
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
            
            {parsedContacts.length > 50 && (
              <div style={{ textAlign: 'center', padding: '12px', color: 'var(--hs-gray-600)', fontSize: '0.875rem' }}>
                ... y {parsedContacts.length - 50} contactos más
              </div>
            )}
          </div>
        </Modal.Body>
        
        <Modal.Footer>
          <button 
            className={`${styles.btn} ${styles.btnSecondary}`}
            onClick={() => setModalStep('mapping')}
          >
            <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
            Atrás
          </button>
          <button 
            className={`${styles.btn} ${styles.btnPrimary}`}
            onClick={handleImport}
            disabled={validCount === 0}
          >
            Importar {validCount} contactos
            <FontAwesomeIcon icon={faArrowRight} className="ms-2" />
          </button>
        </Modal.Footer>
      </>
    );
  };

  // ==================== 🎨 RENDER: IMPORTING STEP ====================

  const renderImportingStep = () => (
    <>
      <Modal.Header style={{ 
        backgroundColor: 'var(--bs-body-bg)',
        borderBottom: '1px solid var(--bs-border-color)'
      }}>
        <Modal.Title style={{ color: 'var(--bs-body-color)' }}>
          Importando Contactos...
        </Modal.Title>
      </Modal.Header>
      
      <Modal.Body style={{ 
        backgroundColor: 'var(--bs-body-bg)',
        color: 'var(--bs-body-color)'
      }}>
        <div style={{ textAlign: 'center', padding: '40px 20px' }}>
          <FontAwesomeIcon 
            icon={faSpinner}
            spin
            style={{ fontSize: '3rem', color: 'var(--hs-orange)', marginBottom: '24px' }}
          />
          <h5 style={{ fontWeight: 600, marginBottom: '16px' }}>
            Importando contactos...
          </h5>
          <ProgressBar 
            now={progress} 
            label={`${progress}%`}
            style={{ height: '24px' }}
            variant="success"
          />
          <p style={{ color: 'var(--hs-gray-600)', fontSize: '0.875rem', marginTop: '16px' }}>
            Por favor espera mientras procesamos los contactos
          </p>
        </div>
      </Modal.Body>
    </>
  );

  // ==================== 🎨 RENDER: COMPLETE STEP ====================

  const renderCompleteStep = () => (
    <>
      <Modal.Header closeButton style={{ 
        backgroundColor: 'var(--bs-body-bg)',
        borderBottom: '1px solid var(--bs-border-color)'
      }}>
        <Modal.Title style={{ color: 'var(--bs-body-color)' }}>
          <FontAwesomeIcon icon={faCheckCircle} className="me-2" style={{ color: 'var(--hs-success)' }} />
          Importación Completa
        </Modal.Title>
      </Modal.Header>
      
      <Modal.Body style={{ 
        backgroundColor: 'var(--bs-body-bg)',
        color: 'var(--bs-body-color)'
      }}>
        <div style={{ textAlign: 'center', padding: '40px 20px' }}>
          <FontAwesomeIcon 
            icon={faCheckCircle}
            style={{ fontSize: '4rem', color: 'var(--hs-success)', marginBottom: '24px' }}
          />
          
          <h4 style={{ fontWeight: 600, marginBottom: '24px' }}>
            ¡Importación Exitosa!
          </h4>
          
          {importResult && (
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(3, 1fr)', 
              gap: '16px',
              marginBottom: '24px'
            }}>
              <div style={{ 
                padding: '20px', 
                backgroundColor: 'var(--hs-success)', 
                color: 'white',
                borderRadius: '8px'
              }}>
                <div style={{ fontSize: '2.5rem', fontWeight: 'bold' }}>{importResult.success}</div>
                <div style={{ fontSize: '0.875rem' }}>Importados</div>
              </div>
              
              <div style={{ 
                padding: '20px', 
                backgroundColor: 'var(--hs-warning)', 
                color: 'white',
                borderRadius: '8px'
              }}>
                <div style={{ fontSize: '2.5rem', fontWeight: 'bold' }}>{importResult.skipped}</div>
                <div style={{ fontSize: '0.875rem' }}>Omitidos</div>
              </div>
              
              <div style={{ 
                padding: '20px', 
                backgroundColor: 'var(--hs-danger)', 
                color: 'white',
                borderRadius: '8px'
              }}>
                <div style={{ fontSize: '2.5rem', fontWeight: 'bold' }}>{importResult.errors}</div>
                <div style={{ fontSize: '0.875rem' }}>Errores</div>
              </div>
            </div>
          )}
          
          <p style={{ color: 'var(--hs-gray-600)', fontSize: '0.875rem' }}>
            Los contactos han sido agregados a tu base de datos y están listos para usar.
          </p>
        </div>
      </Modal.Body>
      
      <Modal.Footer>
        <button 
          className={`${styles.btn} ${styles.btnPrimary}`}
          onClick={handleClose}
        >
          Cerrar
        </button>
      </Modal.Footer>
    </>
  );

  // ==================== 🎨 RENDER PRINCIPAL ====================

  // Z-index fijo MUY alto para estar siempre al frente
  const modalZIndex = 999999;
  const backdropZIndex = 999998;

  const modalContent = (
    <Modal 
      show={show} 
      onHide={handleClose}
      size="lg"
      centered
      backdrop={modalStep === 'importing' ? 'static' : true}
      keyboard={modalStep !== 'importing'}
      style={{ zIndex: modalZIndex }}
      backdropClassName="upload-csv-backdrop"
      dialogClassName="upload-csv-dialog"
    >
      <style>{`
        .upload-csv-backdrop {
          z-index: ${backdropZIndex} !important;
          position: fixed !important;
        }
        .upload-csv-dialog {
          z-index: ${modalZIndex} !important;
          position: relative !important;
        }
        .modal.show {
          z-index: ${modalZIndex} !important;
        }
      `}</style>
      {modalStep === 'upload' && renderUploadStep()}
      {modalStep === 'mapping' && renderMappingStep()}
      {modalStep === 'preview' && renderPreviewStep()}
      {modalStep === 'importing' && renderImportingStep()}
      {modalStep === 'complete' && renderCompleteStep()}
    </Modal>
  );

  // Renderizar modal en document.body usando Portal (fuera del árbol DOM de ventanas)
  return show ? createPortal(modalContent, document.body) : null;
};

export default UploadCSVModal;